import React from 'react';

/**
 * 손님 화면 공용 디자인 키트 — 대표 확정 최고 기준(2026-11-03 "이 디자인기준최고야"):
 * 지식도서관 knowledge/web-design/premium-web-3d-motion-guide-2026-10-14.md.
 *
 * 처음 만든 곳은 인스타그램 계정 연결 화면(../instagram/connectFlowUi.tsx)이고, 여기서는 다른 화면이 쓰기 쉽게
 * 이름만 바꿔 다시 내보낸다(모양·동작은 한 곳에서 고친다). 여기서 새로 더한 것은 입력칸·이름표·알림 줄뿐이다.
 *  - 카드 안 카드·유리 효과·빛 번짐·그라데이션 버튼을 쓰지 않는다. 구획은 헤어라인.
 *  - 주 버튼은 흰 캡슐 하나. 보조는 외곽선 캡슐이나 글자 단추.
 *  - 입력 글씨는 18px 이상(아이폰 확대 방지).
 *  - 손님에게 보이는 글귀는 예전 화면 그대로 쓴다 — 디자인만 바꾼다.
 */
export {
    EASE_OUT,
    EASE_SPRING,
    ConnectTitle as KitTitle,
    ConnectSheet as KitSheet,
    ConnectPrimaryButton as KitPrimaryButton,
    ConnectSecondaryButton as KitSecondaryButton,
    ConnectTextButton as KitTextButton,
    ConnectFootnote as KitFootnote,
    ConnectError as KitError,
    ConnectRow as KitRow,
    InstagramGlyph as KitInstagramGlyph,
} from '../instagram/connectFlowUi';

/** 입력칸 이름표 */
export const KitLabel: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[#A1A1AA]">
        {children}
    </label>
);

/** 입력칸 틀 — 높이 64px·모서리 16. 왼쪽 아이콘, 가운데 입력, 오른쪽 덧붙임. 상태에 따라 테두리만 바뀐다. */
export const KitField: React.FC<{
    icon?: React.ReactNode;
    right?: React.ReactNode;
    state?: 'default' | 'error' | 'ok';
    children: React.ReactNode;
    className?: string;
}> = ({ icon, right, state = 'default', children, className = '' }) => (
    <div
        className={`flex h-16 items-center gap-3 rounded-2xl border bg-[#0F0F11] px-5 transition-colors ${
            state === 'error'
                ? 'border-[#F87171]/60'
                : state === 'ok'
                    ? 'border-[#34D399]/50'
                    : 'border-white/[0.10] focus-within:border-white/30'
        } ${className}`}
    >
        {icon && <span className="shrink-0 text-[#52525B]">{icon}</span>}
        <div className="min-w-0 flex-1">{children}</div>
        {right && <span className="shrink-0">{right}</span>}
    </div>
);

/** 알림 한 줄 — 상자 대신 점 + 글씨(성공은 초록, 오류는 빨강) */
export const KitNotice: React.FC<{ tone: 'success' | 'error'; children: React.ReactNode }> = ({ tone, children }) => (
    <div
        className={`flex items-start gap-2 text-[14px] leading-relaxed ${tone === 'success' ? 'text-[#34D399]' : 'text-[#F87171]'}`}
        role={tone === 'error' ? 'alert' : 'status'}
    >
        <span className={`mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full ${tone === 'success' ? 'bg-[#34D399]' : 'bg-[#F87171]'}`} />
        <span className="min-w-0 break-keep">{children}</span>
    </div>
);
